import { useEffect, useState, useCallback } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import {
  FiPlus, FiEdit2, FiTrash2, FiCheck, FiEyeOff, FiArrowLeft, FiKey, FiCopy,
  FiRefreshCw, FiAlertTriangle, FiLoader, FiArrowUpRight, FiSearch,
} from 'react-icons/fi'
import toast from 'react-hot-toast'
import { useAuth } from '../../contexts/AuthContext'
import BlogAdminEditor from './BlogAdminEditor'

const fetchAuth = (url, options = {}) => {
  const token = localStorage.getItem('token')
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  })
}

const TABS = [
  { id: 'posts', label: 'Posts' },
  { id: 'api', label: 'API key' },
]

export default function BlogAdmin() {
  const { empleado } = useAuth()
  const [tab, setTab] = useState('posts')

  // Solo super_admin
  if (!empleado) return <Navigate to="/login" replace />
  if (empleado.rol !== 'super_admin') return <Navigate to="/v2" replace />

  return (
    <>
      <Helmet>
        <title>Admin Blog | PresenzIA</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/v2" className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
                <FiArrowLeft className="h-4 w-4" /> Volver
              </Link>
              <h1 className="text-xl font-bold text-gray-900">Admin · Blog</h1>
            </div>
            <Link to="/blog" target="_blank" rel="noopener noreferrer"
              className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1">
              Ver blog público <FiArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          <div className="max-w-7xl mx-auto px-6 flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-8">
          {tab === 'posts' && <PostsTab />}
          {tab === 'api' && <ApiKeyTab />}
        </main>
      </div>
    </>
  )
}

function PostsTab() {
  const [posts, setPosts] = useState([])
  const [categorias, setCategorias] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtroEstado, setFiltroEstado] = useState('all')
  const [busqueda, setBusqueda] = useState('')
  const [editor, setEditor] = useState(null) // { mode: 'create'|'edit', slug? }

  const cargar = useCallback(() => {
    setLoading(true)
    const params = new URLSearchParams()
    if (filtroEstado !== 'all') params.set('estado', filtroEstado)
    if (busqueda) params.set('q', busqueda)
    params.set('page_size', '100')
    fetchAuth(`/api/blog/admin/posts?${params}`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => setPosts(data.posts || []))
      .catch((e) => toast.error(`No se pudieron cargar los posts: ${e.message}`))
      .finally(() => setLoading(false))
  }, [filtroEstado, busqueda])

  useEffect(() => {
    cargar()
  }, [cargar])

  useEffect(() => {
    fetchAuth('/api/blog/categorias')
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setCategorias(data.categorias || []) })
      .catch(() => {})
  }, [])

  const togglePublish = async (post) => {
    const accion = post.estado === 'published' ? 'unpublish' : 'publish'
    try {
      const res = await fetchAuth(`/api/blog/posts/${encodeURIComponent(post.slug)}/${accion}`, { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast.success(accion === 'publish' ? 'Publicado' : 'Despublicado')
      cargar()
    } catch (e) {
      toast.error(`No se pudo ${accion === 'publish' ? 'publicar' : 'despublicar'}: ${e.message}`)
    }
  }

  const borrar = async (post) => {
    if (!window.confirm(`¿Borrar "${post.titulo}"? Esta acción NO se puede deshacer.`)) return
    try {
      const res = await fetchAuth(`/api/blog/posts/${encodeURIComponent(post.slug)}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast.success('Post borrado')
      cargar()
    } catch (e) {
      toast.error(`No se pudo borrar: ${e.message}`)
    }
  }

  return (
    <>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
            className="px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm">
            <option value="all">Todos los estados</option>
            <option value="published">Publicados</option>
            <option value="draft">Borradores</option>
          </select>
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input type="text" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar por título o excerpt"
              className="pl-9 pr-3 py-2 rounded-lg border border-gray-200 bg-white text-sm w-64" />
          </div>
        </div>
        <button onClick={() => setEditor({ mode: 'create' })}
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg text-sm">
          <FiPlus className="h-4 w-4" /> Nuevo post
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-center py-12">
          <FiLoader className="h-6 w-6 mx-auto animate-spin text-gray-400" />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-200">
          <p className="text-gray-500 text-sm">Sin posts. Crea el primero con "Nuevo post".</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                <th className="px-4 py-3 text-left">Título</th>
                <th className="px-4 py-3 text-left">Categoría</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Publicación</th>
                <th className="px-4 py-3 text-right">Vistas</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.slug} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-semibold text-gray-900">{p.titulo}</div>
                    <div className="text-xs text-gray-500 font-mono">{p.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{p.categoria?.nombre || '—'}</td>
                  <td className="px-4 py-3">
                    {p.estado === 'published' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Publicado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-xs font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" /> Borrador
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 font-mono text-xs">{p.fecha_publicacion || '—'}</td>
                  <td className="px-4 py-3 text-right text-gray-600 font-mono">{p.view_count || 0}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditor({ mode: 'edit', slug: p.slug })}
                        title="Editar"
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <FiEdit2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => togglePublish(p)}
                        title={p.estado === 'published' ? 'Despublicar' : 'Publicar'}
                        className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg">
                        {p.estado === 'published'
                          ? <FiEyeOff className="h-3.5 w-3.5" />
                          : <FiCheck className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => borrar(p)}
                        title="Borrar"
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <FiTrash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editor && (
        <BlogAdminEditor
          mode={editor.mode}
          slug={editor.slug}
          categorias={categorias}
          onClose={() => setEditor(null)}
          onSaved={() => cargar()}
        />
      )}
    </>
  )
}

function ApiKeyTab() {
  const [info, setInfo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generando, setGenerando] = useState(false)
  const [keyNueva, setKeyNueva] = useState(null)

  const cargar = useCallback(() => {
    setLoading(true)
    fetchAuth('/api/blog/api-key/info')
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setInfo)
      .catch((e) => toast.error(`No se pudo cargar info de la API key: ${e.message}`))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { cargar() }, [cargar])

  const rotar = async () => {
    if (info?.configurada && !window.confirm('Esto invalidará la clave actual. ¿Continuar?')) return
    setGenerando(true)
    try {
      const res = await fetchAuth('/api/blog/api-key/rotate', { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setKeyNueva(data.key)
      cargar()
    } catch (e) {
      toast.error(`No se pudo rotar: ${e.message}`)
    } finally {
      setGenerando(false)
    }
  }

  const revocar = async () => {
    if (!window.confirm('Esto deshabilitará la API. Los agentes IA dejarán de poder publicar. ¿Continuar?')) return
    try {
      const res = await fetchAuth('/api/blog/api-key', { method: 'DELETE' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      toast.success('API key revocada')
      cargar()
    } catch (e) {
      toast.error(`No se pudo revocar: ${e.message}`)
    }
  }

  const copiar = (val) => {
    navigator.clipboard.writeText(val).then(() => toast.success('Copiado al portapapeles'))
  }

  if (loading) {
    return <div className="text-center py-12"><FiLoader className="h-6 w-6 mx-auto animate-spin text-gray-400" /></div>
  }

  return (
    <div className="max-w-3xl">
      {/* Banner clave nueva */}
      {keyNueva && (
        <div className="mb-6 rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <FiAlertTriangle className="flex-shrink-0 h-5 w-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-amber-900 mb-1">Guarda esta clave AHORA</h3>
              <p className="text-sm text-amber-800 mb-3">
                No volverás a verla. Configúrala en Hermes/OpenClaw como variable de entorno
                <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">PRESENZIA_BLOG_API_KEY</code>
                o en el header <code className="mx-1 px-1 py-0.5 bg-amber-100 rounded text-xs">X-API-Key</code> de cada request.
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm font-mono break-all">
                  {keyNueva}
                </code>
                <button onClick={() => copiar(keyNueva)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg">
                  <FiCopy className="h-4 w-4" /> Copiar
                </button>
              </div>
              <button onClick={() => setKeyNueva(null)}
                className="mt-3 text-xs text-amber-700 hover:text-amber-900 underline">
                He guardado la clave (ocultar)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Estado */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
            <FiKey className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-gray-900 mb-1">API key del blog</h2>
            <p className="text-sm text-gray-600 mb-5">
              Permite a un agente IA externo (Hermes, OpenClaw…) crear, actualizar y publicar posts en el blog vía
              <code className="mx-1 px-1 py-0.5 bg-gray-100 rounded text-xs">X-API-Key</code> header. Solo hay una clave activa a la vez.
            </p>

            {info?.configurada ? (
              <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
                <Row label="Estado">
                  <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Activa
                  </span>
                </Row>
                <Row label="Prefix">
                  <code className="font-mono text-gray-600">{info.prefix || '—'}…</code>
                </Row>
                <Row label="Último uso">
                  <span className="font-mono text-gray-600">{info.last_used_at || 'Nunca'}</span>
                </Row>
                <Row label="Última IP">
                  <span className="font-mono text-gray-600">{info.last_used_ip || '—'}</span>
                </Row>
              </div>
            ) : (
              <div className="text-sm text-gray-500 border-t border-gray-100 pt-4">
                No hay clave configurada. Genera una para que un agente IA pueda publicar posts.
              </div>
            )}

            <div className="mt-6 flex items-center gap-2">
              <button onClick={rotar} disabled={generando}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-sm disabled:opacity-50">
                {generando ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiRefreshCw className="h-4 w-4" />}
                {info?.configurada ? 'Rotar clave' : 'Generar clave'}
              </button>
              {info?.configurada && (
                <button onClick={revocar}
                  className="inline-flex items-center gap-1.5 px-4 py-2 border border-red-300 text-red-700 hover:bg-red-50 font-semibold rounded-lg text-sm">
                  <FiTrash2 className="h-4 w-4" /> Revocar
                </button>
              )}
            </div>

            <div className="mt-6 pt-6 border-t border-gray-100">
              <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Para tu agente</h3>
              <p className="text-sm text-gray-600 mb-2">
                Endpoint para que el agente lea la spec al inicio de cada sesión:
              </p>
              <code className="block px-3 py-2 bg-gray-100 rounded-lg text-xs font-mono text-gray-700">
                GET https://presenzia.app/api/blog/shortcodes
              </code>
              <p className="text-sm text-gray-600 mt-3 mb-2">Endpoint para crear posts:</p>
              <code className="block px-3 py-2 bg-gray-100 rounded-lg text-xs font-mono text-gray-700">
                POST https://presenzia.app/api/blog/posts<br />
                Headers: X-API-Key: blog_xxx, Content-Type: application/json
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5">
      <span className="text-gray-500">{label}</span>
      {children}
    </div>
  )
}
